import { reactive, computed } from 'vue';
import { callAPI } from '@/store/socket';

// Every file the SPA attaches goes straight to S3 by a presigned PUT, through this one queue: images and
// files of comments, images of new and transferred tasks, edited images and screen recordings. The files of
// one task are announced and saved in batches; each file is uploaded and retried on its own. The API stores
// the attachment only once its file is in S3, so an upload cut halfway leaves nothing behind.
const PARALLEL_BATCHES = 2;
const PARALLEL_UPLOADS = 2;
// as many as the API takes in one call
const MAX_FILES_PER_CALL = 10;
// a large file on a slow link may take long; only a pause in the bytes means the request hung
const STALL_MS = 30e3;
const RETRY_DELAYS_S = [2, 5, 15, 30, 60];
const DONE_SHOWN_MS = 1500;

export const uploads = reactive([]);

export const uploadsProgress = computed(() => {
  const counted = uploads.filter(u => u.state !== 'failed');
  const total = counted.reduce((sum, u) => sum + u.total, 0);
  const loaded = counted.reduce((sum, u) => sum + u.loaded, 0);
  return total ? Math.floor((loaded / total) * 100) : 0;
});

export const hasUnfinishedUploads = computed(() => uploads.some(u => u.state !== 'done'));

export const MAX_UPLOAD_RETRIES = RETRY_DELAYS_S.length;

function put(upload, url) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let stallTimer = null;
    const finish = (result) => {
      clearTimeout(stallTimer);
      resolve(result);
    };
    const watchStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        xhr.abort();
        finish({ reason: `no progress for ${STALL_MS / 1000} s` });
      }, STALL_MS);
    };
    xhr.upload.onprogress = (event) => {
      upload.loaded = event.loaded;
      watchStall();
    };
    xhr.onload = () => finish({ status: xhr.status });
    xhr.onerror = () => finish({ reason: 'network error' });
    xhr.open('PUT', url);
    // the API picks the type the file is stored with by its extension; one it doesn't know becomes a download
    xhr.setRequestHeader('Content-Type', upload.contentType);
    const disposition = upload.contentType === 'application/octet-stream' ? 'attachment' : 'inline';
    const asciiName = upload.name.replace(/[^\x20-\x7e]/g, '_').replace(/"/g, '');
    xhr.setRequestHeader('Content-Disposition', `${disposition}; filename="${asciiName}"; filename*=UTF-8''${encodeURIComponent(upload.name)}`);
    xhr.setRequestHeader('Cache-Control', 'no-transform, public, max-age=31536000');
    watchStall();
    xhr.send(upload.file);
  });
}

const isBusy = upload => ['announcing', 'uploading', 'uploaded', 'saving'].includes(upload.state);

function pump() {
  if (!navigator.onLine) {
    return;
  }
  const batchesRunning = new Set(uploads.filter(isBusy).map(u => u.batch)).size;
  for (let started = batchesRunning; started < PARALLEL_BATCHES; started += 1) {
    const first = uploads.find(u => u.state === 'queued');
    if (!first) {
      return;
    }
    const batch = Symbol('batch');
    const files = uploads
      .filter(u => u.state === 'queued' && u.taskId === first.taskId)
      .slice(0, MAX_FILES_PER_CALL);
    files.forEach((u) => {
      u.state = 'announcing';
      u.batch = batch;
    });
    runBatch(first.taskId, files);
  }
}

// refused by the API or the storage, or every attempt spent: the queue stops trying and the person sees why
function fail(upload, reason, limit) {
  upload.state = 'failed';
  upload.error = { reason, limit };
  console.error('Upload refused', { attachmentId: upload.id, name: upload.name, reason, limit });
}

// failAs: what the person reads once every attempt is spent
function retry(upload, failAs, reason, immediately = false) {
  upload.loaded = 0;
  if (!navigator.onLine) {
    // resumed by the 'online' listener below, without spending an attempt
    upload.state = 'offline';
    return;
  }
  if (upload.retries === RETRY_DELAYS_S.length) {
    console.error('Upload failed', { attachmentId: upload.id, name: upload.name, reason });
    fail(upload, failAs);
    return;
  }
  const delayMs = immediately ? 0 : RETRY_DELAYS_S[upload.retries] * 1000 * (0.8 + Math.random() * 0.4);
  upload.retries += 1;
  upload.state = 'retry';
  console.warn('Upload retry', { attachmentId: upload.id, name: upload.name, reason, attempt: upload.retries, delayMs });
  setTimeout(() => {
    upload.state = 'queued';
    pump();
  }, delayMs);
}

async function uploadFile(upload) {
  upload.state = 'uploading';
  upload.loaded = 0;
  const { status, reason } = await put(upload, upload.uploadUrl);
  if (status === 403) {
    // the URL expired while waiting for a retry: the next attempt asks for a new one
    retry(upload, 'unreachable_storage', 'S3 403', true);
    return;
  }
  if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
    fail(upload, 'storage_refused');
    return;
  }
  if (!status || status >= 400) {
    retry(upload, 'unreachable_storage', reason || `S3 ${status}`);
    return;
  }
  upload.loaded = upload.total;
  upload.state = 'uploaded';
}

async function uploadFiles(files) {
  const waiting = [...files];
  const worker = async () => {
    while (waiting.length) {
      await uploadFile(waiting.shift());
    }
  };
  await Promise.all(Array.from({ length: PARALLEL_UPLOADS }, worker));
}

async function runBatch(taskId, files) {
  const announced = await callAPI({
    method: 'getAttachmentUploadUrls',
    params: {
      taskId,
      attachments: files.map(u => ({ ...u.attachment, size: u.total })),
    },
    async: true,
  });
  if (announced.error) {
    files.forEach(u => retry(u, 'server_error', `upload URL: ${announced.error}`));
    pump();
    return;
  }
  files.forEach((upload, index) => {
    const { rejected, limit, uploadUrl, contentType } = announced[index];
    if (rejected) {
      fail(upload, rejected, limit);
      return;
    }
    upload.uploadUrl = uploadUrl;
    upload.contentType = contentType;
  });

  await uploadFiles(files.filter(u => u.state === 'announcing'));
  const toSave = files.filter(u => u.state === 'uploaded');
  if (!toSave.length) {
    pump();
    return;
  }

  toSave.forEach((u) => { u.state = 'saving'; });
  // the synchronous queue keeps it after the comments the attachments belong to
  const saved = await callAPI({
    method: 'saveAttachments',
    params: {
      taskId,
      attachments: toSave.map(u => ({ ...u.attachment, convertationRequired: u.convertationRequired })),
    },
    persisted: true,
  });
  if (saved.error) {
    toSave.forEach(u => retry(u, 'server_error', `save: ${saved.error}`));
    pump();
    return;
  }
  toSave.forEach((upload, index) => {
    const { rejected, limit } = saved[index];
    if (rejected) {
      fail(upload, rejected, limit);
      return;
    }
    upload.state = 'done';
    setTimeout(() => uploads.splice(uploads.indexOf(upload), 1), DONE_SHOWN_MS);
  });
  pump();
}

// upload: { file, name, taskId, attachment, convertationRequired }
export function enqueueUpload(upload) {
  uploads.push({
    ...upload,
    id: upload.attachment.id,
    total: upload.file.size,
    loaded: 0,
    retries: 0,
    state: 'queued',
  });
  // the files of one action (the images of a new task, the files of a comment) are added one after another:
  // the batch is made once they are all queued
  setTimeout(pump);
}

export function retryUpload(upload) {
  upload.retries = 0;
  upload.error = null;
  upload.state = 'queued';
  pump();
}

export function dropUpload(upload) {
  uploads.splice(uploads.indexOf(upload), 1);
}

window.addEventListener('online', () => {
  uploads.filter(u => u.state === 'offline').forEach((u) => { u.state = 'queued'; });
  pump();
});
